import type { BroadcastContent } from '../../../api/types';
import type { TrainType } from '../../../data/routes';

import { TRAIN_TYPE_DEFAULT, TRAIN_TYPE_META } from '../constants';

/** `BroadcastContent.train_type` の値 (`undefined` を除いたもの)。 */
type TrainTypeValue = NonNullable<BroadcastContent['train_type']>;

/**
 * コンテンツ単位の列車種別 (`train_type`) 選択欄。
 *
 * - 選択肢は `TRAIN_TYPE_META` (全列車 / 快速 / 各駅停車) を、線区が持つ種別
 *   (`availableTrainTypes` = `RouteMaster.train_types`) で絞り込んで表示する。
 *   「全列車」(`'all'`) は線区固有の属性ではなく **全線区共通の既定値** なので
 *   絞り込みの対象外とし、常に先頭の選択肢として出す。
 *   → `availableTrainTypes` が空 (= 線区マスタに `train_types` が無い) の場合は
 *     「全列車」のみが表示され、快速 / 各駅停車は選択肢に現れない
 *     (受入条件「快速・各駅は列車種別がある線区のみで表示・設定できる」)。
 * - 「全列車」を選ぶと全営業列車が放送対象になる。種別不明の列車 (回送・貨物・
 *   GTFS 欠損日) は Core 側の絞り込みで放送対象にはならない。
 * - 値変更時は親コンポーネントに種別または `undefined` を通知する。親側で
 *   `'all' === TRAIN_TYPE_DEFAULT` の場合は `train_type` プロパティを削除する
 *   (未指定と同義 = DynamoDB item 削減 + 既存データと bit-perfect 互換)。
 *   サーバ側 (`lambda/control-panel-api/index.py` の `_coerce_train_type`) も
 *   `'all'` を `None` に落として保存しないため、フロント / サーバで挙動が一致する。
 *
 * a11y: `<select>` に `aria-label` を付与し、種別を持たない線区では `disabled` +
 * 補足ヒントで「選べない理由」が分かるようにする。
 * なお `disabled` はあくまで補助であり、**種別を持たない線区で快速 / 各駅停車の
 * option をそもそも生成しない** ことが受入条件の本質。
 */
export function TrainTypeField({
  contentIndex,
  value,
  availableTrainTypes,
  onChange,
}: {
  /** 親コンテンツの index (data-testid 用) */
  contentIndex: number;
  /** 現在の train_type (undefined = 未指定 = 全列車) */
  value: BroadcastContent['train_type'];
  /**
   * この放送地点の線区が持つ列車種別 (`RouteMaster.train_types`)。
   * 空配列 = 種別を持たない線区 → 「全列車」のみ選択可。
   */
  availableTrainTypes: readonly TrainType[];
  /** 値が変わったら呼ばれる (undefined = 未指定 / 'all' = 全列車) */
  onChange: (nextType: BroadcastContent['train_type']) => void;
}): React.JSX.Element {
  // 現在値。未指定なら 'all' (全列車) を表示する。
  const effective: TrainTypeValue = value ?? TRAIN_TYPE_DEFAULT;

  // 表示する選択肢。
  // - 'all' は常に表示 (全線区共通の既定値)。
  // - 'rapid' / 'local' は線区が持つ種別のみ表示。
  // - 現在値が候補に無い場合 (マスタ変更後の既存レコード等) は、<select> が
  //   空表示になって値が勝手に変わったように見えるのを避けるため option を残す
  //   (カテゴリ <select> の絞り込みと同じ防御策)。
  const options = TRAIN_TYPE_META.filter(
    (m) =>
      m.value === TRAIN_TYPE_DEFAULT ||
      availableTrainTypes.includes(m.value as TrainType) ||
      m.value === effective,
  );

  // 種別を持たない線区 = 実質「全列車」固定なので操作させない。
  const hasTrainTypes = options.length > 1;

  return (
    // PlaybackRateField と同じ「見出し + 入力 + ヒント」を 1 段の flex 行にまとめる
    // レイアウト。狭幅では flexWrap で自然に折り返す。
    <div
      className="train-type-field"
      data-testid={`content-train-type-field-${contentIndex}`}
      style={{
        margin: '6px 0',
        padding: '6px 8px',
        background: '#f8f9fa',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <strong>列車種別</strong>
      <select
        aria-label={`コンテンツ#${contentIndex + 1} 列車種別`}
        data-testid={`content-train-type-select-${contentIndex}`}
        value={effective}
        disabled={!hasTrainTypes}
        onChange={(e) => onChange(e.target.value as TrainTypeValue)}
      >
        {options.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <span className="hint" style={{ fontSize: 12, color: '#666' }}>
        {hasTrainTypes
          ? '(全列車=この線区の全営業列車。回送・貨物など種別不明の列車は対象外)'
          : '(この線区には列車種別がないため「全列車」固定です)'}
      </span>
    </div>
  );
}
